import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Text,
  VStack,
  Heading,
  useToast,
  Container,
  Flex,
  Divider,
  Input,
  Select,
} from "@chakra-ui/react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  getShippingVolumeForecastBySKU,
  getDeliveryTimeForecast,
  getReturnRateForecast,
  getCostPerItemForecast,
  getSkuList,
  VolumeEntry,
} from "../service/ForecastService";

interface DistributionForecastingProps {
  onClose: () => void;
  onOpenAdvanced: () => void;
}

const DistributionForecasting: React.FC<DistributionForecastingProps> = ({
  onClose,
  onOpenAdvanced,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const [duration, setDuration] = useState<string>("Day");
  const [deliveryDays, setDeliveryDays] = useState<number | null>(null);
  const [returnRate, setReturnRate] = useState<number | null>(null);
  const [avgCostPerItem, setAvgCostPerItem] = useState<number | null>(null);
  const [shippingVolume, setShippingVolume] = useState<
    { date: string; volume: number; type: "historical" | "forecasted" }[]
  >([]);

  const [futureDate, setFutureDate] = useState<string>(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 2);
    return tomorrow.toISOString().split("T")[0];
  });

  const [skuList, setSkuList] = useState<{ skuItem: string; value: string }[]>(
    []
  );
  const [selectedSku, setSelectedSku] = useState<string>("");

  const fetchForecastData = async () => {
    try {
      if (!selectedSku) {
        toast({ title: "Please select a SKU", status: "warning" });
        return;
      }

      const volume = await getShippingVolumeForecastBySKU(
        selectedSku,
        duration
      );

      const today = new Date();
      const forecastedWithDates = volume
        .filter((item) => item.type === "forecasted")
        .map((item, index) => ({
          ...item,
          date: new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() + index * 7
          )
            .toISOString()
            .split("T")[0],
        }));

      const historical = volume.filter((item) => item.type === "historical");

      setShippingVolume([...historical, ...forecastedWithDates]);

      const [delivery, returnRateData, cost] = await Promise.all([
        getDeliveryTimeForecast(),
        getReturnRateForecast(),
        getCostPerItemForecast(),
      ]);

      setDeliveryDays(delivery?.predictedDeliveryDays);
      setReturnRate(returnRateData?.returnRate);
      setAvgCostPerItem(cost?.averageCostPerItem);
    } catch (err) {
      toast({ title: "Failed to load forecast data", status: "error" });
    }
  };

  useEffect(() => {
    getSkuList().then(setSkuList);
    fetchForecastData();
  }, []);

  const downloadPDF = async () => {
    if (!chartRef.current) return;
    const canvas = await html2canvas(chartRef.current);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF();
    pdf.addImage(imgData, "PNG", 10, 10, 180, 90);
    pdf.save("distribution-forecast.pdf");
  };

  return (
    <Container maxW="4xl" py={6}>
      <Heading size="lg" mb={4}>
        📦 Distribution Forecasting
      </Heading>

      {/* <Flex wrap="wrap" gap={6} mb={6}>
        <Button colorScheme="blue" onClick={downloadPDF}>
          📄 Download PDF
        </Button>
      </Flex> */}

      <Flex wrap="wrap" gap={4} align="center" mb={4}>
        <Box>
          <Text fontWeight="medium" mb={1}>
            Select SKU:
          </Text>
          <Select
            placeholder="Select SKU"
            value={selectedSku}
            onChange={(e) => setSelectedSku(e.target.value)}
          >
            {skuList.map((sku) => (
              <option key={sku.value} value={sku.value}>
                {sku.skuItem}
              </option>
            ))}
          </Select>
        </Box>

        <Box>
          <Text fontWeight="medium" mb={1}>
            Select Duration:
          </Text>
          <Select
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
          >
            <option value="Day">1 Day After</option>
            <option value="Week">1 Week After</option>
            <option value="Month">1 Month After</option>
            <option value="Year">1 Year After</option>
          </Select>
        </Box>

        <Button colorScheme="teal" onClick={fetchForecastData}>
          🔄 Refresh Forecast
        </Button>
      </Flex>

      <Divider mb={4} />

      {shippingVolume.length > 0 && (
        <Box
          ref={chartRef}
          p={4}
          borderWidth={1}
          borderRadius="lg"
          bg="white"
          shadow="md"
        >
          <Text fontWeight="bold" mb={2}>
            📈 Shipping Volume Forecast
          </Text>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={shippingVolume.sort(
                (a, b) =>
                  new Date(a.date).getTime() - new Date(b.date).getTime()
              )}
              margin={{ top: 5, right: 20, bottom: 80, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis
                dataKey="date"
                tickFormatter={(str) =>
                  new Date(str).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                  })
                }
                angle={-45}
                textAnchor="end"
                interval={0}
              />

              <YAxis />
              <Tooltip
                labelFormatter={(label) =>
                  `📅 ${new Date(label).toLocaleDateString("en-GB")}`
                }
              />
              <Legend verticalAlign="top" height={36} />

              {/* Historical Line */}
              <Line
                type="monotone"
                dataKey="volume"
                stroke="#3182CE"
                name="Shipping Volume"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
      <Box mt={4}>
        <Button colorScheme="blue" onClick={downloadPDF}>
          Download PDF
        </Button>
      </Box>

      <Box mt={4}>
        <Button
          size="sm"
          variant="link"
          colorScheme="blue"
          onClick={onOpenAdvanced}
        >
          ← Back to Forecast Selection
        </Button>
      </Box>
    </Container>
  );
};

export default DistributionForecasting;
